import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, FormControl, IconButton, ListSubheader, MenuItem, OutlinedInput, Popover, Select, TextField, Typography, useTheme } from '@mui/material'
import { ErrorMessage, Form, Field, Formik } from 'formik'
import * as Yup from 'yup'
import React, { useEffect, useRef, useState } from 'react'
import { getCleanSubjectDetails, getTransformedGroupDetails, Icons } from '../../../utils/utilities'
import { useUserStore } from '../../../store/user.store'
import { ArrowDropDown, CheckBox, CheckBoxOutlineBlankOutlined, RefreshOutlined, RemoveCircle } from '@mui/icons-material'
import { observer } from 'mobx-react'
import { useLiveSessionStore } from '../../../store/livesessions.store'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomSelectField from '../../common/CustomSelectField'
import CustomChip from '../../common/CustomChip'

const accessGroupsFilterFunc = ( item, values, filterEntity ) => {

    if ( filterEntity === "batch" ) {
        if ( item.entity !== 'batch' ) return false

        if ( !item.batch_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( values.sem && item.current_sem && values.sem !== item.current_sem ) return false

        return true
    } else {

        if ( item.entity !== 'section' ) return false

        if ( !item.section_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( item.current_sem && values.sem && values.sem !== item.current_sem ) return false

        return true
    }
}

const subjectFilterFunc = ( sub, values ) => {

    if ( sub.ac_year_id !== values.academicYear ) return false

    return true
}

const CreateClassSessionDialog = observer( ( { state, setState, mode = 'create', session, getData } ) => {

    const [adding, setAdding] = useState( false )
    const [loading, setLoading] = useState( true )
    const [refreshing, setRefreshing] = useState( false )
    const [accessGroupsPopoverAnchor, setAccessGroupsPopoverAnchor] = useState( false )

    const UserStore = useUserStore()
    const LiveSessionStore = useLiveSessionStore()

    const { palette, border, theme } = useTheme()

    const batchSectionInputRef = useRef()

    const initialValues = {
        name: mode === 'edit' ? session.name : '',
        access_groups: mode === 'edit' ? session.access_groups : [],
        access_groups_ids: [],
        subject: null,
        academicYear: UserStore.getUserAcademicYears[0]?.ac_year_id,
        year: '',
        sem: ''
    }
    const [academicYearId, setAcademicYearId] = useState( UserStore.getUserAcademicYears[0]?.ac_year_id || "" )

    const validationSchema = Yup.object().shape( {
        name: Yup.string().required( "Name is required" ).max( 200, "Max length 200 characters" ),
        subject: Yup.mixed().required( "Subject is required" ),
        access_groups: Yup.mixed().required( "Provide atleast one access group" ).test( "TEXT_ACCESS_GROUPS", "Please provide atleast one access group", val => {
            if ( !val || val?.length === 0 ) return false
            return true
        } )
    } )

    const openAccessGroupsPopover = ( e, setError ) => {
        if ( academicYearId )
            setAccessGroupsPopoverAnchor( e.currentTarget )
        else {
            setError( 'access_groups', 'Please select academic year to select batch or section' )
        }
    }

    const handleBatchOrSectionSelectChange = ( e, setFieldValue ) => {
        const value = Array.from( typeof e.target.value === 'string' ? e.target.value.split( "," ) : e.target.value )
        const formattedValues = []
        for ( const ag of value ) {
            const accessGroup = UserStore.getUsersAllBatchesAndSectionList.find( ac => String( ac.id ) === String( ag ) )
            formattedValues.push( accessGroup )
        }

        setFieldValue( 'access_groups_ids', value )
        setFieldValue( 'access_groups', formattedValues )
    }

    const handleAcademicYearChange = async ( e, setValue ) => {
        setAcademicYearId( e.target.value )
        setValue( 'academicYear', e.target.value )
        if ( UserStore.getErrorsStatuses?.academicYearsAndBatches === true )
            await UserStore.fetchUserBatchesAndSection( e.target.value )
    }


    const handleSubjectChange = ( event, setFieldValue ) => {
        const subjectDetails = UserStore.getUserSubjectsDetails?.currentSubjects.find( s => s?.unique_subject_key === event.target.value )
        setFieldValue( 'subject', subjectDetails )
        setFieldValue( 'year', subjectDetails.year )
        setFieldValue( 'sem', subjectDetails.sem )
    }

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }

    const refreshBatches = async () => {
        if ( academicYearId ) {
            setRefreshing( "batches" )
            await UserStore.fetchUserBatchesAndSection( academicYearId, true )
            setRefreshing( null )
        }
    }

    const toggleToAccessGroup = ( values, accessGroup, setValue, selected ) => {
        if ( !selected ) {
            setValue( 'access_groups', [...values.access_groups, { ...accessGroup, id: accessGroup.id }] )
            setValue( 'access_groups_ids', [...values.access_groups_ids, accessGroup.id] )
        } else {
            let index = values.access_groups_ids.findIndex( agId => agId === accessGroup.id )
            if ( index >= 0 ) {
                let newAccessGroups = [...values.access_groups]
                let newAccessGroupsIds = [...values.access_groups_ids]
                newAccessGroups.splice( index, 1 )
                newAccessGroupsIds.splice( index, 1 )
                setValue( 'access_groups', newAccessGroups )
                setValue( 'access_groups_ids', newAccessGroupsIds )
            }
        }
    }

    const createSession = async ( values, { resetForm } ) => {
        setAdding( true )
        const subject = getCleanSubjectDetails( values?.subject )
        if ( await LiveSessionStore.createSession( { ...values, subject } ) ) {
            if ( getData && typeof getData === 'function' )
                getData()
            handleClose( resetForm )
        }
        setAdding( false )
    }

    const updateSession = async ( values, { resetForm } ) => {
        setAdding( true )
        if ( await LiveSessionStore.updateSession( session.id, values ) )
            handleClose( resetForm, getData )
        setAdding( false )
    }

    const handleClose = ( reset, refetch ) => {
        if ( reset )
            reset()
        setState( false )
        if ( refetch )
            refetch()

    }

    useEffect( () => {
        const getData = async () => {
            if ( mode === 'edit' && !session ) {
                return setState( false )
            }
            if ( UserStore.getErrorsStatuses?.subjects === true )
                await UserStore.fetchUserSubjects()
            if ( UserStore.getErrorsStatuses?.academicYears === true )
                await UserStore.fetchUserAcademicYears()
            setLoading( false )
        }
        getData()
    }, [UserStore, mode, session, setState] )


    return (
        <Dialog open={state} slotProps={{
            paper: { sx: { width: "85%", maxWidth: "1024px", background: palette.form.formCardBg } }
        }}>
            <DialogContent sx={{ padding: "0" }}>
                <Box borderBottom={border[1]} padding="10px 20px" >
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{mode === "edit" ? "Update" : "Create new"} class resources repository.</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{mode === "edit" ? `Update ${session.name} details` : `Create a new beyond class resources repository.`} </Typography>
                </Box>
                <Box padding="20px" >
                    <Formik onSubmit={mode === 'create' ? createSession : updateSession} initialValues={initialValues} validationSchema={validationSchema}>
                        {( { values, setFieldValue, resetForm, setFieldError } ) => ( <Form>
                            <FormControl fullWidth>
                                <Typography component="label" htmlFor='name' color={palette.form.labelColor} variant='subtitle2' gutterBottom>Live session name *</Typography>
                                <Field id="name" size="small" type="text" name="name" as={TextField} placeholder="Eg: CSE, 2019 SECTION-A" />
                                <Typography fontSize="12px" color="error"> <ErrorMessage name='name' /> </Typography>
                            </FormControl>


                            <FormControl margin='normal' fullWidth>
                                <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                    <Typography component="label" htmlFor='academicYearInput' gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                        Select academic year *
                                    </Typography>
                                    <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                        {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                    </Button>
                                </Box>
                                <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => handleAcademicYearChange( event, setFieldValue )} value={values.academicYear || ""} />
                            </FormControl>


                            {mode !== "edit" && <FormControl margin='normal' fullWidth>
                                <Typography htmlFor="subject" gutterBottom variant='subtitle2' component="label" color={palette.form.labelColor}>
                                    Select the subject relevant to the class resources *
                                </Typography>

                                <CustomSelectField
                                    id="subjectSelector"
                                    size="small"
                                    renderValue={selected => {
                                        if ( selected ) {
                                            const subject = UserStore.getUserSubjectsDetails?.currentSubjects.find( sub => sub.unique_subject_key === selected )
                                            if ( subject )
                                                return `${subject.subject_name?.toLowerCase()} (${subject.subject_code})`
                                            else
                                                return "Subject"
                                        } else {
                                            return "Subject"
                                        }
                                    }}
                                    itemValueKey="unique_subject_key"
                                    itemIdentityKey="unique_subject_key"
                                    data={( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).filter( sub => subjectFilterFunc( sub, values ) )}
                                    disabled={!values.academicYear}
                                    sx={{ color: !values.subject && palette.form.placeholder, textTransform: "capitalize" }}
                                    value={values?.subject?.unique_subject_key || ""}
                                    onChange={event => handleSubjectChange( event, setFieldValue )}
                                    name="subject"
                                    renderOptionText={item => <Box display="flex" flexDirection="column">
                                        <Typography textTransform="capitalize" variant='subtitle2'>
                                            {item.subject_name?.toLowerCase()}
                                        </Typography>
                                        <Box display="flex" gap="5px" alignItems="center">
                                            <CustomChip tooltipTitle='Subject code' value={item.subject_code} />
                                            <CustomChip tooltipTitle='Year' value={`Year: ${item.year}`} />
                                            {item.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.sem}`} />}
                                        </Box>
                                    </Box>}
                                />
                                <Typography component="span" color="errorMessage.main" marginTop="5px" fontSize="12px"> <ErrorMessage name="subject" /> </Typography>

                            </FormControl>}

                            {( academicYearId || mode === "edit" ) && <FormControl margin='normal' fullWidth>
                                <Typography component="label" htmlFor='batch' variant="subtitle2" color={palette.form.labelColor} gutterBottom >Select Batch / Section *</Typography>

                                <CustomSelectField
                                    onChange={event => handleBatchOrSectionSelectChange( event, setFieldValue )}
                                    options={{ showCheckbox: true }}
                                    slotProps={{
                                        input: {
                                            sx: {
                                                padding: values.access_groups?.length > 0 ? "5px" : ""
                                            }
                                        }
                                    }}
                                    searchFilter={( item, searchTerm ) => {
                                        return item.short_title?.toLowerCase()?.includes( searchTerm?.toLowerCase() )
                                    }}
                                    noOptionsMessage="We couldn't find any sections or batches for the year and semester of the selected subject."
                                    renderValue={value => {
                                        if ( value?.length === 0 ) {
                                            return "Select batches and sections."
                                        } else {
                                            return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: "5px" }}>
                                                {values.access_groups.map( ( ag, index ) => {
                                                    ag = getTransformedGroupDetails( ag )
                                                    return (
                                                        <Box padding="2px 5px" bgcolor={palette.contentBg} border={border[1]} borderRadius="10px" key={ag.id} display="flex" alignItems="center">
                                                            <Typography fontSize="12px" variant='subtitle2'>
                                                                {ag.short_title}
                                                            </Typography>
                                                            <IconButton onMouseDown={e => e.stopPropagation()} onClick={() => toggleToAccessGroup( values, ag, setFieldValue, true )} size='small' color='error'>
                                                                {Icons.default.CancelIcon}
                                                            </IconButton>
                                                        </Box>
                                                    )
                                                } )}
                                            </Box>
                                        }
                                    }}
                                    size="small"
                                    value={values.access_groups_ids || ""}
                                    data={{
                                        sections: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "section" ) ),
                                        batches: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "batch" ) )
                                    }}
                                    id="selectedBatches"
                                    itemValueKey="id"
                                    multiple
                                    renderOptionText={item => <Box display="flex" flexDirection="column">
                                        <Typography variant='subtitle2'>
                                            {item.short_title}
                                        </Typography>
                                        <Box display="flex" gap="5px" alignItems="center">
                                            <CustomChip tooltipTitle='Course short name' value={item.course_name_short} />
                                            <CustomChip tooltipTitle='Year' value={`Year: ${item.current_year}`} />
                                            {item.current_year && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.current_sem}`} />}
                                        </Box>
                                    </Box>}
                                />

                                <Typography fontSize="12px" color="error"> <ErrorMessage name='access_groups' /> </Typography>
                                <Button disabled={refreshing === "batches" || !academicYearId} startIcon={refreshing === "batches" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshBatches} sx={{ width: "fit-content", marginTop: "2px", fontSize: "12px" }} color="secondary" >
                                    {refreshing === "batches" ? "Refreshing batches..." : "Refresh batches"}
                                </Button>
                            </FormControl>}

                            <Box marginTop="30px" display="flex" alignItems="center" gap="10px">
                                <Button type="submit" disabled={adding} startIcon={adding ? <CircularProgress size={14} /> : mode === "edit" ? Icons.default.CheckIcon : Icons.default.AddIcon} variant="contained" disableElevation >{mode === 'edit' ? "Save changes" : "Create resources repository"}</Button>
                                <Button onClick={() => handleClose( resetForm )} variant="outlined" startIcon={Icons.default.CloseIcon} color="error" >Cancel</Button>
                            </Box>
                        </Form> )}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog>
    )
} )

export default CreateClassSessionDialog
